import { useCallback } from "react";
import { FieldValues, Path, PathValue, useFormContext } from "react-hook-form";

import { Input } from "@/components/Form/Base/input.component";

import { useAutofill } from "../../Utils/useAutofill";
import { BaseField } from "../BaseField/BaseField.component";
import { TextFieldProps } from "../TextField/Textfield.component";

type StrictTextFieldProps<T extends FieldValues> = TextFieldProps<T> & {
    incorrectPattern: RegExp;
    inputTransformer?: (value: string) => string;
};

const StrictTextField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    placeholder,
    autoComplete,
    disabled,
    hideLabel,
    hideMicroCopy,
    optionalIndication,
    requiredIndication,
    maxLength,
    incorrectPattern,
    inputTransformer,
    ref,
    test
}: StrictTextFieldProps<T>) => {
    const { setValue } = useFormContext<T>();
    const { handleOnAutofill } = useAutofill<T>(name);

    const preventIncorrectCharachters = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (
                incorrectPattern.test(
                    inputTransformer ? inputTransformer(e.key) : e.key
                )
            ) {
                e.preventDefault();
            }
        },
        [incorrectPattern, inputTransformer]
    );

    const onChangeHandler = useCallback(
        (onChangeCb: (...event: any[]) => void) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const transformedValue = inputTransformer
                    ? inputTransformer(e.target.value)
                    : e.target.value;
                if (incorrectPattern.test(transformedValue)) {
                    setValue(
                        name,
                        transformedValue.replace(
                            incorrectPattern,
                            ""
                        ) as PathValue<T, Path<T>>
                    );
                    return;
                }
                if (inputTransformer) {
                    e.target.value = transformedValue;
                }
                onChangeCb(e);
            },
        [incorrectPattern, inputTransformer, name, setValue]
    );

    return (
        <BaseField<T, Extract<Path<T>, string>>
            name={name}
            label={label}
            microCopy={microCopy}
            required={required}
            hideLabel={hideLabel}
            hideMicroCopy={hideMicroCopy}
            optionalIndication={optionalIndication}
            requiredIndication={requiredIndication}
        >
            {({ field, fieldState }) => {
                return (
                    <Input
                        {...field}
                        type="text"
                        inputMode="text"
                        id={name}
                        onKeyDown={preventIncorrectCharachters}
                        onChange={onChangeHandler(field.onChange)}
                        placeholder={placeholder}
                        autoComplete={autoComplete}
                        valid={fieldState.isValid}
                        invalid={fieldState.isInvalid}
                        disabled={disabled}
                        data-test={test}
                        ref={ref}
                        maxLength={maxLength}
                        onAutofill={handleOnAutofill}
                    />
                );
            }}
        </BaseField>
    );
};

export { StrictTextField };
